import { NoDataIcon as noDataIcon } from '@/shared/ui';
import HrChartOptions from '@/widgets/hr-summary/ui/hrChartOptions';
import DailySummaryEcgViewerWrapper from './dailySummaryEcgViewerWrapper';
import DailySummaryHrChart from './dailySummaryHrChart';
import DailySummaryHrsThumbnail from './dailySummaryHrsThumbnail';
import DailySummaryThumbnail from './dailySummaryThumbnail';
import useHrSummary from './useHrSummary';

export type HrSummaryProps = {
  isEmptyData?: boolean;
};

/**
 * Composes the heart-rate summary view from the daily thumbnail column, the HR chart with its options bar, the ECG viewer and the min/max thumbnail list. Study id, profile id, timezone offset, ECG data map and the chart ref come from useHrSummary, and the chart area is replaced by a no-data panel when isEmptyData is set.
 *
 * @param props - Carries isEmptyData, which swaps the chart area for the empty-state panel.
 * @returns The rendered HR summary layout, or its no-data placeholder when no data was recorded.
 */
const HrSummary = ({ isEmptyData }: HrSummaryProps) => {
  const { profileId, studyId, timezoneOffset, ecgDataMap, hrChartRef, handleClickArrowButton } =
    useHrSummary();

  return (
    <>
      <DailySummaryThumbnail timezoneOffset={timezoneOffset} />
      {isEmptyData ? (
        <div className="mct-daily-summary-no-content">
          <div className="no-data">
            <img className="no-data__icon" src={noDataIcon} alt="No data icon" />
            <div className="no-data__text">There is no data recorded</div>
          </div>
        </div>
      ) : (
        <>
          <div className="mct-daily-summary-content">
            <div className="mct-daily-summary-content-container">
              <HrChartOptions profileId={profileId} timezoneOffset={timezoneOffset} isOldUI />
              <DailySummaryHrChart
                ref={hrChartRef}
                ecgDataMap={ecgDataMap}
                studyId={studyId}
                profileId={profileId}
                timezoneOffset={timezoneOffset}
              />
              <DailySummaryEcgViewerWrapper
                ecgDataMap={ecgDataMap}
                timezoneOffset={timezoneOffset}
                profileId={profileId}
                studyId={studyId}
                handleClickArrowButton={handleClickArrowButton}
              />
            </div>
          </div>
          <DailySummaryHrsThumbnail
            ecgDataMap={ecgDataMap}
            studyId={studyId}
            profileId={profileId}
            timezoneOffset={timezoneOffset}
          />
        </>
      )}
    </>
  );
};

export default HrSummary;
